/**
 * Mock data + date helpers used by both the in-memory and Supabase paths.
 * Kept in its own module so timesheetSupabaseService can import shared
 * constants without inheriting the dispatcher cycle.
 */
import type {
  TsContract,
  TsChargeCode,
  TsEmployee,
  TsTimeEntry,
  TsLaborCategory,
  TsSubmission,
} from '../types/timesheet.types'

export const DEMO_EMPLOYEE_ID = 'ts-emp-001'
export const DEMO_SUPERVISOR_ID = 'ts-emp-002'

/**
 * Default PTO charge code id. The "Fill as PTO" holiday handler upserts
 * 8h on this code for each holiday in the current week. Typical
 * exempt-employee treatment: PTO books to G&A. Org-specific overrides
 * land when `organizations.pto_charge_code_id` exists.
 */
export const PTO_CHARGE_CODE_ID = 'ts-cc-pto'

export const employees: TsEmployee[] = [
  {
    id: 'ts-emp-001',
    name: 'James Mitchell',
    initials: 'JM',
    role: 'Systems Engineer II',
    clearance: 'TS/SCI',
    exempt: true,
    payClass: 'salary',
    scheduleType: '9x80',
    weekStartDay: 'mon',
    authorizedLaborCats: ['SSE-4', 'SSE-5'],
    defaultLaborCategory: 'SSE-5',
    standardHourlyRate: 175,
    weeklySalary: 7000,
    ttaMethod: 'effective',
    supervisorId: 'ts-emp-002',
  },
  {
    id: 'ts-emp-002',
    name: 'Maria Santos',
    initials: 'MS',
    role: 'Project Manager',
    clearance: 'TS',
    exempt: true,
    payClass: 'salary',
    scheduleType: '5x8',
    weekStartDay: 'mon',
    authorizedLaborCats: ['PM-3', 'PM-4'],
    defaultLaborCategory: 'PM-4',
    standardHourlyRate: 155,
    weeklySalary: 6200,
    ttaMethod: 'standard',
    supervisorId: null,
  },
  {
    id: 'ts-emp-003',
    name: 'Thomas Park',
    initials: 'TP',
    role: 'Procurement Specialist',
    clearance: 'Secret',
    exempt: false,
    payClass: 'hourly',
    scheduleType: '4x10',
    weekStartDay: 'mon',
    authorizedLaborCats: ['SE-3'],
    defaultLaborCategory: 'SE-3',
    standardHourlyRate: 125,
    weeklySalary: 5000,
    ttaMethod: 'standard',
    supervisorId: 'ts-emp-002',
  },
  {
    // Featured read-only Auditor persona (AUDITOR_IDS in PersonaSwitcher).
    // Transcribed 2026-06-13 from the live `public.users` row to keep mock
    // mode in sync with supabase mode:
    //   select id, pernr, name, title, department, system_role, pay_class,
    //          schedule_type, exempt from users where id = 'user_004';
    //   -> Linda Nakamura · Compliance & Audit Lead · Compliance · Auditor · salary · 5x8 · exempt
    // Compliance oversight role: no LCAT/salary/clearance in the source row,
    // reports outside the line org (live manager_id = user_010, no mock analog).
    id: 'ts-emp-004',
    pernr: 'ts-emp-004',
    name: 'Linda Nakamura',
    initials: 'LN',
    role: 'Compliance & Audit Lead',
    clearance: 'None',
    exempt: true,
    payClass: 'salary',
    scheduleType: '5x8',
    weekStartDay: 'sun',
    authorizedLaborCats: [],
    supervisorId: null,
  },
  {
    // Stream G demo persona — a sales/business-dev role that doesn't
    // touch the timesheet manually. autoFill posts G&A entries against
    // their daily target each working day; banner on My Timesheet
    // surfaces what's been auto-logged.
    id: 'ts-emp-005',
    name: 'Robert Kim',
    initials: 'RK',
    role: 'G&A Administrator',
    clearance: 'None',
    exempt: true,
    payClass: 'general-time',
    scheduleType: '5x8',
    weekStartDay: 'mon',
    authorizedLaborCats: ['PM-3', 'PM-4'],
    defaultLaborCategory: 'PM-3',
    standardHourlyRate: 95,
    weeklySalary: 3800,
    ttaMethod: 'standard',
    supervisorId: 'ts-emp-002',
    autoFill: {
      enabled: true,
      defaultChargeCodeId: 'ts-cc-005', // G&A — general bucket
      mode: 'auto-submit',
    },
  },
  {
    // All-access demo walkthrough seat. In supabase mode this resolves to
    // user_006 (migration 00306) whose system_role='GlobalAdmin' opens every
    // RBAC-gated page; this mock entry keeps it selectable in local/mock mode
    // and feeds the Settings picker's pernr-dedup. salary · 9x80 so the
    // schedule banner shows; flip payClass on Settings to demo other tracks.
    id: 'ts-emp-006',
    name: 'Avery Quinn',
    initials: 'AQ',
    role: 'Demo Admin — All Access',
    clearance: 'TS/SCI',
    exempt: true,
    payClass: 'salary',
    scheduleType: '9x80',
    weekStartDay: 'mon',
    authorizedLaborCats: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5'],
    defaultLaborCategory: 'PM-4',
    standardHourlyRate: 185,
    weeklySalary: 7400,
    ttaMethod: 'standard',
    supervisorId: null,
  },
]

export const laborCategories: TsLaborCategory[] = [
  { code: 'SSE-4', name: 'Senior Software Engineer IV', minClearance: 'Secret' },
  { code: 'SSE-5', name: 'Principal Software Engineer V', minClearance: 'TS' },
  { code: 'PM-3', name: 'Program Manager III', minClearance: 'Secret' },
  { code: 'PM-4', name: 'Senior Program Manager IV', minClearance: 'Secret' },
  { code: 'SE-3', name: 'Systems Engineer III', minClearance: 'Secret' },
  { code: 'SE-4', name: 'Senior Systems Engineer IV', minClearance: 'TS' },
]

export const contracts: TsContract[] = [
  {
    id: 'ts-con-001',
    code: 'GTEX-A321-2025',
    name: 'A321XLR Avionics Systems',
    agency: 'Gentex Aerospace',
    type: 'CPFF',
    fundedValue: 6_400_000,
    burnedValue: 5_120_000,
    ceiling: 6_800_000,
    periodStart: '2024-01-01',
    periodEnd: '2026-12-31',
    casCovered: true,
    primeOrSub: 'sub',
    cuiMarking: 'CUI//SP-EXPT',
  },
  {
    id: 'ts-con-002',
    code: 'GTEX-787-2024',
    name: '787-10 Mission Integration',
    agency: 'Gentex Aerospace',
    type: 'T&M',
    fundedValue: 2_800_000,
    burnedValue: 1_050_000,
    ceiling: 2_800_000,
    periodStart: '2024-06-01',
    periodEnd: '2026-05-31',
    casCovered: false,
    primeOrSub: 'prime',
  },
  {
    id: 'ts-con-003',
    code: 'GTEX-RD-ADV',
    name: 'Advanced Propulsion R&D',
    agency: 'Internal / DARPA BAA',
    type: 'CPIF',
    fundedValue: 1_200_000,
    burnedValue: 330_000,
    ceiling: 1_400_000,
    periodStart: '2025-01-01',
    periodEnd: '2027-06-30',
    casCovered: true,
    primeOrSub: 'prime',
  },
  {
    id: 'ts-con-004',
    code: 'IND-GA',
    name: 'General & Administrative',
    agency: 'Internal',
    type: 'Indirect',
    fundedValue: 0,
    burnedValue: 0,
    ceiling: 0,
    periodEnd: null,
    casCovered: false,
    primeOrSub: 'prime',
  },
]

export const chargeCodes: TsChargeCode[] = [
  {
    id: 'ts-cc-001',
    contractId: 'ts-con-001',
    wbs: '1.1.1',
    description: 'A321XLR – Software Dev & Integration',
    laborCategories: ['SSE-4', 'SSE-5'],
    allowable: true,
    requiresClearance: 'Secret',
  },
  {
    id: 'ts-cc-002',
    contractId: 'ts-con-001',
    wbs: '1.1.2',
    description: 'A321XLR – Systems Engineering',
    laborCategories: ['SE-3', 'SE-4'],
    allowable: true,
    requiresClearance: 'Secret',
  },
  {
    id: 'ts-cc-003',
    contractId: 'ts-con-002',
    wbs: '2.1.1',
    description: '787-10 – Integration & Test',
    laborCategories: ['SSE-4', 'SE-4'],
    allowable: true,
  },
  {
    id: 'ts-cc-004',
    contractId: 'ts-con-003',
    wbs: '3.1.1',
    description: 'Propulsion R&D – Software',
    laborCategories: ['SSE-4', 'SSE-5'],
    allowable: true,
  },
  {
    id: 'ts-cc-005',
    contractId: 'ts-con-004',
    wbs: 'G&A',
    description: 'General & Administrative',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
  {
    id: 'ts-cc-006',
    contractId: 'ts-con-004',
    wbs: 'B&P',
    description: 'Bid & Proposal',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5'],
    allowable: true,
    pool: 'B&P',
  },
  {
    id: 'ts-cc-pto',
    contractId: 'ts-con-004',
    wbs: 'PTO-VAC',
    description: 'Paid Time Off — Vacation / Holiday',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
  {
    id: 'ts-cc-pto-sick',
    contractId: 'ts-con-004',
    wbs: 'PTO-SICK',
    description: 'Paid Time Off — Sick Leave',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
  {
    id: 'ts-cc-pto-brv',
    contractId: 'ts-con-004',
    wbs: 'PTO-BRV',
    description: 'Paid Time Off — Bereavement',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
  {
    id: 'ts-cc-pto-mil',
    contractId: 'ts-con-004',
    wbs: 'PTO-MIL',
    description: 'Paid Time Off — Military Leave',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
  {
    id: 'ts-cc-pto-jury',
    contractId: 'ts-con-004',
    wbs: 'PTO-JURY',
    description: 'Paid Time Off — Jury Duty',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
  {
    id: 'ts-cc-pto-fmla',
    contractId: 'ts-con-004',
    wbs: 'PTO-FMLA',
    description: 'Paid Time Off — FMLA',
    laborCategories: ['PM-3', 'PM-4', 'SSE-4', 'SSE-5', 'SE-3', 'SE-4'],
    allowable: true,
    pool: 'G&A',
  },
]

export function getMonday(d: Date): Date {
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  const m = new Date(d)
  m.setDate(d.getDate() + diff)
  m.setHours(0, 0, 0, 0)
  return m
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

export function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

// ─── Demo time entries (current week starting Mon 2026-04-28) ────────────────

export const seedTimeEntries: TsTimeEntry[] = [
  // Alex Rivera — week of 2026-04-28
  { id: 'te-001', employeeId: 'ts-emp-001', date: '2026-04-28', chargeCodeId: 'ts-cc-001', laborCategory: 'SSE-5', hours: 8 },
  { id: 'te-002', employeeId: 'ts-emp-001', date: '2026-04-29', chargeCodeId: 'ts-cc-001', laborCategory: 'SSE-5', hours: 8 },
  { id: 'te-003', employeeId: 'ts-emp-001', date: '2026-04-30', chargeCodeId: 'ts-cc-001', laborCategory: 'SSE-5', hours: 6 },
  { id: 'te-004', employeeId: 'ts-emp-001', date: '2026-04-30', chargeCodeId: 'ts-cc-005', laborCategory: 'SSE-5', hours: 2 },
  { id: 'te-005', employeeId: 'ts-emp-001', date: '2026-05-01', chargeCodeId: 'ts-cc-001', laborCategory: 'SSE-5', hours: 7.5 },
  { id: 'te-006', employeeId: 'ts-emp-001', date: '2026-05-01', chargeCodeId: 'ts-cc-004', laborCategory: 'SSE-5', hours: 0.5 },
  { id: 'te-007', employeeId: 'ts-emp-001', date: '2026-05-02', chargeCodeId: 'ts-cc-001', laborCategory: 'SSE-5', hours: 8 },
  // Taylor Brooks — prior week (submitted, pending approval)
  { id: 'te-101', employeeId: 'ts-emp-003', date: '2026-04-21', chargeCodeId: 'ts-cc-002', laborCategory: 'SE-3', hours: 8, submissionId: 'sub-001' },
  { id: 'te-102', employeeId: 'ts-emp-003', date: '2026-04-22', chargeCodeId: 'ts-cc-002', laborCategory: 'SE-3', hours: 8, submissionId: 'sub-001' },
  { id: 'te-103', employeeId: 'ts-emp-003', date: '2026-04-23', chargeCodeId: 'ts-cc-002', laborCategory: 'SE-3', hours: 10, submissionId: 'sub-001' },
  { id: 'te-104', employeeId: 'ts-emp-003', date: '2026-04-24', chargeCodeId: 'ts-cc-002', laborCategory: 'SE-3', hours: 8, submissionId: 'sub-001' },
  { id: 'te-105', employeeId: 'ts-emp-003', date: '2026-04-25', chargeCodeId: 'ts-cc-002', laborCategory: 'SE-3', hours: 6, submissionId: 'sub-001' },
  // Morgan Davis — prior week (submitted, pending approval, has anomaly)
  { id: 'te-201', employeeId: 'ts-emp-004', date: '2026-04-21', chargeCodeId: 'ts-cc-003', laborCategory: 'SSE-4', hours: 8, submissionId: 'sub-002' },
  { id: 'te-202', employeeId: 'ts-emp-004', date: '2026-04-22', chargeCodeId: 'ts-cc-003', laborCategory: 'SSE-4', hours: 12.5, submissionId: 'sub-002' },
  { id: 'te-203', employeeId: 'ts-emp-004', date: '2026-04-23', chargeCodeId: 'ts-cc-001', laborCategory: 'SSE-4', hours: 8, submissionId: 'sub-002' },
  { id: 'te-204', employeeId: 'ts-emp-004', date: '2026-04-24', chargeCodeId: 'ts-cc-003', laborCategory: 'SSE-4', hours: 8, submissionId: 'sub-002' },
  { id: 'te-205', employeeId: 'ts-emp-004', date: '2026-04-25', chargeCodeId: 'ts-cc-003', laborCategory: 'SSE-4', hours: 8, submissionId: 'sub-002' },
]

export const seedSubmissions: TsSubmission[] = [
  {
    id: 'sub-001',
    employeeId: 'ts-emp-003',
    weekStart: '2026-04-21',
    totalHours: 40,
    status: 'pending',
    submittedAt: '2026-04-26T09:15:00Z',
    anomalyCount: 1,
  },
  {
    id: 'sub-002',
    employeeId: 'ts-emp-004',
    weekStart: '2026-04-21',
    totalHours: 44.5,
    status: 'pending',
    submittedAt: '2026-04-26T11:30:00Z',
    anomalyCount: 2,
  },
  {
    id: 'sub-003',
    // weekStart aligned to ts-emp-001 (Alex Rivera)'s actual logged entries
    // (te-001..te-007, 40h across 2026-04-28→05-02 on charge codes ts-cc-001
    // /004/005 = WBS 1.1.1 / 3.1.1 / G&A). Window is [weekStart, +7), so the Mon
    // 2026-04-27 start captures them — making this approved submission post real
    // labor to SAP (#63). Previously 2026-04-14 (no entries → empty post).
    employeeId: 'ts-emp-001',
    weekStart: '2026-04-27',
    totalHours: 40,
    status: 'approved',
    submittedAt: '2026-05-04T08:00:00Z',
    anomalyCount: 0,
  },
]
